import type { GameState, QueuedIntent, StateChangeDetail, StateChangeEvent, TilePosition } from '../model/gamestate/GameState';
import { generateClientIntentId } from '../model/gamestate/intentIds';
import {
  buildInputsFromSelection,
  normalizeInputTypes,
  type SpatialSelection,
  type Input,
  type InputType,
} from '../model/Input';
import type { TileMapRenderer, ActorHoverChangedEvent, ActorSelectedEvent, ActorTargetDetail, TileHoverChangedEvent } from '../renderer/tilemap/TileMapRenderer';
import type { InputController, InputIntentDetail } from './InputController';
import type { EventHub } from '../events/EventHub';
import { INPUT_CLEAR_EVENT, INPUT_INTENT_EVENT } from './InputController';
import type { SpaceTimeClient } from '../spacetime/SpaceTimeClient';
import { ActionLogger } from '../utils/ActionLogger';
import { normalizeRotation, calculateTargetAngleFromTileCenters } from '../utils/rotation';
import { t } from '../i18n';
import { SKILL_TARGETING_STATE_EVENT, type SkillTargetCandidate, type SkillTargetingState } from '../events/SkillTargetingEvents';
import { findMovePath, getEffectiveMoveDistance } from './movementPathfinding';

export interface IntentControllerConfig {
  client: SpaceTimeClient;
  characterId: string;
  instanceId: string;
  getTickTarget: () => number;
}

type ClientIntentPayload = Parameters<SpaceTimeClient['intents']['enqueueIntent']>[0]['payload'];

type EnqueueIntentPayload = ClientIntentPayload & (
  | { type: 'moveToTile'; position: TilePosition }
  | { type: 'turn'; direction: number; position: TilePosition }
  | { type: 'interact'; position: TilePosition }
  | { type: 'useSkill'; skillId: string; inputTypes: InputType[]; inputs: Input[] }
  | { type: 'useItem'; itemId: string; position: TilePosition; inputTypes: InputType[]; inputs: Input[] }
);

/**
 * IntentController bridges user input to game state and intent queue
 *
 * Responsibilities:
 * - Listen for input events (clicks, keyboard)
 * - Validate moves locally (bounds, walkability)
 * - Add destinations to local GameState queue
 * - Enqueue intents to server (for future intent-based movement)
 * - Update UI based on local queue state
 */
export class IntentController {
  private readonly inputController: InputController;
  private readonly renderer: TileMapRenderer;
  private readonly gameState: GameState;
  private readonly events: EventHub;
  private readonly statusEl: HTMLElement | null;
  private readonly config: IntentControllerConfig;
  private transitionLocked = false;
  private actionTargeting: {
    kind: 'skill' | 'item';
    actionId: string;
    label: string;
    inputTypes: InputType[];
    slotTypes: Array<'tile' | 'entity'>;
    fixedInputs: Input[];
    requiredSlots: number;
    hovered: SkillTargetCandidate | null;
    selected: Array<SkillTargetCandidate | null>;
  } | null = null;

  constructor(
    inputController: InputController,
    renderer: TileMapRenderer,
    gameState: GameState,
    events: EventHub,
    config: IntentControllerConfig,
    statusEl?: HTMLElement | null
  ) {
    // Validate configuration to catch common mistakes
    if (!config.characterId || config.characterId.length === 0) {
      throw new Error('[IntentController] characterId must not be empty');
    }
    if (!config.instanceId || config.instanceId.length === 0) {
      throw new Error('[IntentController] instanceId must not be empty');
    }

    this.inputController = inputController;
    this.renderer = renderer;
    this.gameState = gameState;
    this.events = events;
    this.config = config;
    this.statusEl = statusEl ?? null;

    this.events.on(INPUT_INTENT_EVENT, this.handleInput);
    this.events.on(INPUT_CLEAR_EVENT, this.handleClearRequest);
    this.gameState.addEventListener('stateChanged', this.handleStateChange as EventListener);
    this.renderer.addEventListener('actorHoverChanged', this.handleActorHoverChanged as EventListener);
    this.renderer.addEventListener('tileHoverChanged', this.handleTileHoverChanged as EventListener);
    this.renderer.addEventListener('actorSelected', this.handleActorSelected as EventListener);
    this.renderer.addEventListener('skillTargetingCancelled', this.handleSkillTargetingCancelled as EventListener);
  }

  public dispose(): void {
    this.events.off(INPUT_INTENT_EVENT, this.handleInput);
    this.events.off(INPUT_CLEAR_EVENT, this.handleClearRequest);
    this.gameState.removeEventListener('stateChanged', this.handleStateChange as EventListener);
    this.renderer.removeEventListener('actorHoverChanged', this.handleActorHoverChanged as EventListener);
    this.renderer.removeEventListener('tileHoverChanged', this.handleTileHoverChanged as EventListener);
    this.renderer.removeEventListener('actorSelected', this.handleActorSelected as EventListener);
    this.renderer.removeEventListener('skillTargetingCancelled', this.handleSkillTargetingCancelled as EventListener);
  }

  private handleInput = (detail: InputIntentDetail): void => {
    if (this.transitionLocked) {
      this.setStatus(t('intentStatus.transitionInProgress'));
      return;
    }

    switch (detail.type) {
      case 'moveToTile': {
        if (this.actionTargeting) {
          if (this.getCurrentTargetSlotType() === 'tile') {
            this.handleTileTargetSelected(detail.position);
          } else {
            this.setStatus('Select a target entity (right-click to cancel)');
          }
          break;
        }
        this.enqueueMovePath(detail.position);
        break;
      }
      case 'turn': {
        const tickTarget = this.config.getTickTarget();
        const intentId = generateClientIntentId();
        this.gameState.enqueueTurn(detail.direction, intentId);
        this.enqueueTurnIntent(detail.direction, tickTarget, intentId);
        break;
      }
      case 'interact': {
        if (!this.confirmPortalTransitionIfNeeded()) {
          this.setStatus('Portal transition cancelled');
          return;
        }
        this.handleInteract();
        const tickTarget = this.config.getTickTarget();
        this.enqueueInteractIntent(tickTarget);
        break;
      }
      case 'useSkill': {
        const inputTypes = detail.inputTypes ?? this.getSkillInputs(detail.skillId);
        const preselectedTarget = this.extractTargetFromInputs(detail.inputs ?? []);
        this.handleActionRequest(
          'skill',
          detail.skillId,
          inputTypes,
          preselectedTarget.position,
          preselectedTarget.entityId,
          detail.inputs ?? []
        );
        break;
      }
      case 'useItem': {
        const preselectedTarget = this.extractTargetFromInputs(detail.inputs ?? []);
        this.handleActionRequest('item', detail.itemId, this.getItemInputs(detail.inputTypes), preselectedTarget.position, preselectedTarget.entityId);
        break;
      }
      case 'useItemOnTile': {
        this.handleActionRequest('item', detail.itemId, ['tile'], detail.targetPosition);
        break;
      }
    }
  };

  private handleInteract(): void {
    const pos = this.gameState.getCharacterPosition();
    const portal = this.gameState.getPortalAt(pos.x, pos.y);

    if (portal) {
      this.setStatus(`Interacting with portal to ${portal.destMapId}...`);
    } else {
      this.setStatus('Nothing to interact with');
    }
  }

  private confirmPortalTransitionIfNeeded(): boolean {
    const pos = this.gameState.getCharacterPosition();
    const portal = this.gameState.getPortalAt(pos.x, pos.y);
    if (!portal) {
      return true;
    }

    const currentMapKind = this.gameState.getMap().mapKind;
    const destMapKind = portal.destMapKind;
    if (typeof currentMapKind !== 'string' || typeof destMapKind !== 'string') {
      return true;
    }

    if (currentMapKind === 'town' && destMapKind !== 'town') {
      return window.confirm(t('intentStatus.portalConfirmLeaveTown'));
    }
    if (currentMapKind !== 'town' && destMapKind === 'town') {
      return window.confirm(t('intentStatus.portalConfirmEnterTown'));
    }
    return true;
  }

  private handleClearRequest = (): void => {
    this.cancelActionTargeting();
    this.gameState.clearPendingQueue();
    // Also clear server-side queue
    const { client, characterId } = this.config;

    ActionLogger.attempt('Clear intent queue');
    void client.intents.clearIntentQueue(characterId)
      .then(() => ActionLogger.success('Clear intent queue'))
      .catch((error) => ActionLogger.failure('Clear intent queue', error));
  };

  private enqueueRemoteIntent(position: TilePosition, tickTarget: number, intentId: string): void {
    this.enqueueIntentWithLogging(
      'Enqueue move',
      tickTarget,
      { type: 'moveToTile', position },
      { x: position.x, y: position.y },
      { intentId, type: 'moveToTile', position },
      intentId
    );
  }

  private enqueueMovePath(target: TilePosition): void {
    const steps = this.resolveMoveSteps(target);
    if (steps === null) {
      this.setStatus(t('intentStatus.cannotMove', { reason: 'no-path' }));
      return;
    }

    for (const step of steps) {
      this.enqueueRequiredTurnsForMove(step);
      const intentId = generateClientIntentId();
      const accepted = this.gameState.enqueueDestination(step, intentId);
      if (!accepted) {
        return;
      }
      this.enqueueRemoteIntent(step, this.config.getTickTarget(), intentId);
    }
  }

  private resolveMoveSteps(target: TilePosition): TilePosition[] | null {
    const map = this.gameState.getMap();
    const grid = this.gameState.getGrid();
    const start = this.gameState.getQueueCursor();
    const moveDistance = this.gameState.getMoveDistance();
    const effectiveMoveDistance = getEffectiveMoveDistance(map, start, moveDistance);

    if (grid.distance(start.x, start.y, target.x, target.y) <= effectiveMoveDistance) {
      return [target];
    }

    return findMovePath(map, grid, start, target, moveDistance, this.gameState.getQueueFrontRotation());
  }

  private enqueueTurnIntent(direction: number, tickTarget: number, intentId: string): void {
    const position = this.gameState.getQueueFrontPosition();
    const normalizedDirection = ((direction % 360) + 360) % 360;
    this.enqueueIntentWithLogging(
      'Turn',
      tickTarget,
      { type: 'turn', direction, position: { x: position.x, y: position.y } },
      { direction },
      { intentId, type: 'turn', direction: normalizedDirection, position: { x: position.x, y: position.y } },
      intentId
    );
  }

  private enqueueInteractIntent(tickTarget: number): void {
    const position = this.gameState.getQueueFrontPosition();
    this.enqueueIntentWithLogging(
      'Interact',
      tickTarget,
      { type: 'interact', position: { x: position.x, y: position.y } }
    );
  }



  private enqueueSkillIntent(
    skillId: string,
    tickTarget: number,
    inputTypes: InputType[],
    selections: SpatialSelection[] = [],
    fixedInputs: Input[] = []
  ): void {
    const queueFront = this.gameState.getQueueFrontPosition();
    const position = selections[0]?.position ?? queueFront;
    const inputs = buildInputsFromSelection(inputTypes, selections, fixedInputs);
    this.enqueueIntentWithLogging(
      'Use Skill',
      tickTarget,
      {
        type: 'useSkill',
        skillId,
        inputTypes,
        inputs,
      },
      { skillId, x: position.x, y: position.y }
    );
  }

  private getTargetSlots(inputTypes: InputType[]): number {
    return inputTypes.filter((input) => input === 'tile' || input === 'entity').length;
  }

  private getSlotTypes(inputTypes: InputType[]): Array<'tile' | 'entity'> {
    return inputTypes.filter((input): input is 'tile' | 'entity' => input === 'tile' || input === 'entity');
  }

  private getSkillInputs(skillId: string): InputType[] {
    const skill = this.gameState.getKnownSkills().find(s => s.skillId === skillId);
    if (!skill) return [];
    return this.parseActionInputs(skill.inputsJson);
  }

  private getItemInputs(inputTypes?: InputType[] | string[]): InputType[] {
    return this.parseActionInputs(inputTypes);
  }

  private parseActionInputs(rawInputs?: string | string[] | InputType[]): InputType[] {
    return normalizeInputTypes(rawInputs);
  }

  private handleTileTargetSelected(position: TilePosition): void {
    if (!this.actionTargeting) return;
    if (this.getCurrentTargetSlotType() !== 'tile') return;
    const firstEmptySlot = this.getCurrentTargetSlotIndex();
    if (firstEmptySlot === -1) {
      return;
    }
    const selectedTile = this.toTileTargetCandidate(position);
    this.actionTargeting.selected[firstEmptySlot] = selectedTile;
    const hasMissingSlots = this.actionTargeting.selected.some((entry) => entry === null);
    this.actionTargeting.hovered = hasMissingSlots ? null : selectedTile;
    this.publishSkillTargetingState();

    if (hasMissingSlots) {
      return;
    }

    const tickTarget = this.config.getTickTarget();
    this.enqueueActionIntent(
      this.actionTargeting.kind,
      this.actionTargeting.actionId,
      tickTarget,
      this.actionTargeting.inputTypes,
      this.collectSpatialSelections(),
      this.actionTargeting.fixedInputs
    );
    this.cancelActionTargeting();
  }

  private getActionTargetingLabel(kind: 'skill' | 'item', actionId: string): string {
    if (kind === 'skill') {
      const skill = this.gameState.getKnownSkills().find(s => s.skillId === actionId);
      return skill ? skill.displayName : 'Skill';
    }
    return actionId;
  }

  private beginActionTargeting(
    kind: 'skill' | 'item',
    actionId: string,
    inputTypes: InputType[],
    fixedInputs: Input[] = []
  ): void {
    const requiredSlots = this.getTargetSlots(inputTypes);
    const slotTypes = this.getSlotTypes(inputTypes);
    const label = this.getActionTargetingLabel(kind, actionId);
    this.actionTargeting = {
      kind,
      actionId,
      label,
      inputTypes,
      slotTypes,
      fixedInputs,
      requiredSlots,
      hovered: null,
      selected: Array.from({ length: requiredSlots }, () => null),
    };
    this.renderer.setSkillTargetingActive(true);
    this.setStatus(`Select a target for ${label} (right-click to cancel)`);
    this.publishSkillTargetingState();
  }

  private cancelActionTargeting(): void {
    if (!this.actionTargeting) {
      return;
    }
    this.actionTargeting = null;
    this.renderer.setSkillTargetingActive(false);
    const queueLength = this.gameState.getPendingQueue().length;
    this.setStatus(
      queueLength
        ? t('intentStatus.queueLength', { count: queueLength })
        : t('intentStatus.noPending')
    );
    this.publishSkillTargetingState();
  }

  private publishSkillTargetingState(): void {
    const state: SkillTargetingState = this.actionTargeting
      ? {
        active: true,
        skillId: this.actionTargeting.actionId,
        requiredSlots: this.actionTargeting.requiredSlots,
        slotTypes: [...this.actionTargeting.slotTypes],
        hovered: this.actionTargeting.hovered,
        selected: this.actionTargeting.selected.map((entry) => (entry ? { ...entry } : null)),
      }
      : {
        active: false,
        skillId: null,
        requiredSlots: 0,
        slotTypes: [],
        hovered: null,
        selected: [],
      };
    this.events.emit(SKILL_TARGETING_STATE_EVENT, state);
  }

  private toSkillTargetCandidate(actor: ActorTargetDetail): SkillTargetCandidate {
    return {
      targetType: 'entity',
      actorId: actor.actorId,
      kind: actor.kind,
      displayName: actor.displayName,
      position: { x: actor.position.x, y: actor.position.y },
    };
  }

  private toTileTargetCandidate(position: TilePosition): SkillTargetCandidate {
    return {
      targetType: 'tile',
      displayName: `${position.x}, ${position.y}`,
      position: { ...position },
    };
  }

  private getCurrentTargetSlotIndex(): number {
    if (!this.actionTargeting) {
      return -1;
    }
    return this.actionTargeting.selected.findIndex((entry) => entry === null);
  }

  private getCurrentTargetSlotType(): 'tile' | 'entity' | null {
    if (!this.actionTargeting) {
      return null;
    }
    const currentIndex = this.getCurrentTargetSlotIndex();
    if (currentIndex === -1) {
      return null;
    }
    return this.actionTargeting.slotTypes[currentIndex] ?? null;
  }

  private handleActorHoverChanged = (event: Event): void => {
    if (!this.actionTargeting) {
      return;
    }
    if (this.getCurrentTargetSlotType() !== 'entity') {
      return;
    }
    const detail = (event as ActorHoverChangedEvent).detail;
    this.actionTargeting.hovered = detail.actor ? this.toSkillTargetCandidate(detail.actor) : null;
    const label = this.actionTargeting.label;
    if (this.actionTargeting.hovered) {
      this.setStatus(`${label} target: ${this.actionTargeting.hovered.displayName}`);
    } else {
      this.setStatus(`Select a target for ${label} (right-click to cancel)`);
    }
    this.publishSkillTargetingState();
  };

  private handleTileHoverChanged = (event: Event): void => {
    if (!this.actionTargeting) {
      return;
    }
    if (this.getCurrentTargetSlotType() !== 'tile') {
      return;
    }
    const detail = (event as TileHoverChangedEvent).detail;
    this.actionTargeting.hovered = detail.tile ? this.toTileTargetCandidate(detail.tile) : null;
    const label = this.actionTargeting.label;
    if (this.actionTargeting.hovered) {
      this.setStatus(`${label} target: ${this.actionTargeting.hovered.displayName}`);
    } else {
      this.setStatus(`Select a target for ${label} (right-click to cancel)`);
    }
    this.publishSkillTargetingState();
  };

  private handleActorSelected = (event: Event): void => {
    if (!this.actionTargeting) {
      return;
    }
    if (this.getCurrentTargetSlotType() !== 'entity') {
      return;
    }
    event.preventDefault();
    const selectedActor = this.toSkillTargetCandidate((event as ActorSelectedEvent).detail);
    const firstEmptySlot = this.actionTargeting.selected.findIndex((entry) => entry === null);
    if (firstEmptySlot === -1) {
      return;
    }
    this.actionTargeting.selected[firstEmptySlot] = selectedActor;
    const hasMissingSlots = this.actionTargeting.selected.some((entry) => entry === null);
    this.actionTargeting.hovered = hasMissingSlots ? null : selectedActor;
    this.publishSkillTargetingState();

    if (hasMissingSlots) {
      return;
    }

    const tickTarget = this.config.getTickTarget();
    this.enqueueActionIntent(
      this.actionTargeting.kind,
      this.actionTargeting.actionId,
      tickTarget,
      this.actionTargeting.inputTypes,
      this.collectSpatialSelections(),
      this.actionTargeting.fixedInputs
    );
    this.cancelActionTargeting();
  };

  private handleActionRequest(
    kind: 'skill' | 'item',
    actionId: string,
    inputTypes: InputType[],
    preselectedTargetPosition?: TilePosition,
    preselectedTargetEntityId?: string,
    fixedInputs: Input[] = []
  ): void {
    const requiredSlots = this.getTargetSlots(inputTypes);
    const preselectedSelections = this.extractSelectionsFromInputs(fixedInputs);
    if (preselectedSelections.length === 0 && preselectedTargetPosition) {
      preselectedSelections.push(
        preselectedTargetEntityId
          ? { type: 'entity', position: preselectedTargetPosition, entityId: preselectedTargetEntityId }
          : { type: 'tile', position: preselectedTargetPosition }
      );
    }
    if (requiredSlots > 0) {
      if (preselectedSelections.length >= requiredSlots) {
        const tickTarget = this.config.getTickTarget();
        this.enqueueActionIntent(
          kind,
          actionId,
          tickTarget,
          inputTypes,
          preselectedSelections,
          fixedInputs
        );
        return;
      }
      if (this.actionTargeting?.kind === kind && this.actionTargeting.actionId === actionId) {
        this.cancelActionTargeting();
      } else {
        this.beginActionTargeting(kind, actionId, inputTypes, fixedInputs);
      }
      return;
    }

    const tickTarget = this.config.getTickTarget();
    this.enqueueActionIntent(kind, actionId, tickTarget, inputTypes, [], fixedInputs);
  }

  private enqueueActionIntent(
    kind: 'skill' | 'item',
    actionId: string,
    tickTarget: number,
    inputTypes: InputType[],
    selections: SpatialSelection[] = [],
    fixedInputs: Input[] = []
  ): void {
    if (kind === 'skill') {
      this.enqueueSkillIntent(actionId, tickTarget, inputTypes, selections, fixedInputs);
      return;
    }
    this.enqueueItemIntent(actionId, tickTarget, inputTypes, selections);
  }

  private handleSkillTargetingCancelled = (): void => {
    this.cancelActionTargeting();
  };

  private enqueueItemIntent(
    itemId: string,
    tickTarget: number,
    inputTypes: InputType[],
    selections: SpatialSelection[] = []
  ): void {
    const queueFront = this.gameState.getQueueFrontPosition();
    const position = { x: queueFront.x, y: queueFront.y };
    const inputs = buildInputsFromSelection(inputTypes, selections);
    const firstSelection = selections[0];
    this.enqueueIntentWithLogging(
      'Use Item',
      tickTarget,
      {
        type: 'useItem',
        itemId,
        position,
        inputTypes,
        inputs,
      },
      { itemId, targetX: firstSelection?.position.x, targetY: firstSelection?.position.y, targetEntityId: firstSelection?.entityId }
    );
  }

  private collectSpatialSelections(): SpatialSelection[] {
    if (!this.actionTargeting) {
      return [];
    }
    return this.actionTargeting.selected.flatMap((entry) => {
      if (!entry) {
        return [];
      }
      if (entry.targetType === 'entity' && entry.actorId) {
        return [{ type: 'entity' as const, position: entry.position, entityId: entry.actorId }];
      }
      return [{ type: 'tile' as const, position: entry.position }];
    });
  }

  private extractSelectionsFromInputs(inputs: Input[]): SpatialSelection[] {
    return inputs.flatMap((input) => {
      if (input.kind === 'entity') {
        return [{ type: 'entity' as const, position: { x: input.x, y: input.y }, entityId: input.entityId }];
      }
      if (input.kind === 'tile') {
        return [{ type: 'tile' as const, position: { x: input.x, y: input.y } }];
      }
      return [];
    });
  }

  private enqueueIntentWithLogging(
    action: string,
    tickTarget: number,
    payload: EnqueueIntentPayload,
    details?: Record<string, unknown>,
    rollbackIntent?: QueuedIntent,
    intentId?: string
  ): void {
    const { client, characterId, instanceId } = this.config;
    ActionLogger.attempt(action, details ? { details } : undefined);
    void client.intents.enqueueIntent({
      characterId,
      instanceId,
      tickTarget,
      payload,
      intentId
    })
      .then(() => ActionLogger.success(action))
      .catch((error) => {
        ActionLogger.failure(action, error);
        if (rollbackIntent) {
          this.gameState.removeOneLocalPendingIntent(rollbackIntent);
        }
      });
  }

  private enqueueRequiredTurnsForMove(target: TilePosition): void {
    if (!this.gameState.getMap().requireFacingForMove) {
      return;
    }
    const grid = this.gameState.getGrid();
    const queueFront = this.gameState.getQueueFrontPosition();
    if (queueFront.x === target.x && queueFront.y === target.y) {
      return;
    }
    if (!grid.isAdjacent(queueFront.x, queueFront.y, target.x, target.y)) {
      return;
    }

    const targetAngle = calculateTargetAngleFromTileCenters(
      grid,
      { x: queueFront.x, y: queueFront.y },
      target
    );
    const desiredFacing = grid.snapFacing(targetAngle);
    let currentFacing = grid.snapFacing(queueFront.rotation);
    if (currentFacing === desiredFacing) {
      return;
    }

    const step = grid.rotationStep;
    const normalizedCurrent = normalizeRotation(currentFacing);
    const normalizedDesired = normalizeRotation(desiredFacing);
    const clockwiseDelta = (normalizedDesired - normalizedCurrent + 360) % 360;
    const counterClockwiseDelta = (normalizedCurrent - normalizedDesired + 360) % 360;
    const turnDirection = clockwiseDelta <= counterClockwiseDelta ? 'right' : 'left';
    const turnsToQueue = Math.floor(Math.min(clockwiseDelta, counterClockwiseDelta) / step);

    for (let i = 0; i < turnsToQueue; i += 1) {
      currentFacing = grid.getNextEdgeFacing(currentFacing, turnDirection);
      const intentId = generateClientIntentId();
      this.gameState.enqueueTurn(currentFacing, intentId);
      this.enqueueTurnIntent(currentFacing, this.config.getTickTarget(), intentId);
    }
  }

  private extractTargetFromInputs(inputs: Input[]): {
    position?: TilePosition;
    entityId?: string;
  } {
    let position: TilePosition | undefined;
    let entityId: string | undefined;
    for (const input of inputs) {
      if (input.kind === 'tile') {
        position = { x: input.x, y: input.y };
      }
      if (input.kind === 'entity') {
        position = { x: input.x, y: input.y };
        if (typeof input.entityId === 'string' && input.entityId.length > 0) {
          entityId = input.entityId;
        }
      }
    }
    return { position, entityId };
  }

  private handleStateChange = (event: Event): void => {
    const customEvent = event as StateChangeEvent;
    const detail = customEvent.detail;
    switch (detail.type) {
      case 'pendingQueueUpdated': {
        // Map the full queue to preserve stable indexing for numbering.
        // Non-movement intents are mapped to null so UIRenderer can skip them
        // while maintaining the correct index for movement markers.
        const positions = detail.queue.map((intent) => {
          const type = intent.type.toLowerCase();
          if (type === 'movetotile') {
            const pos = (intent as any).position;
            if (pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
              return pos;
            }
          }
          return null;
        });

        this.renderer.setQueuePreview(positions);
        const totalQueueLen = detail.queue.length;
        this.setStatus(
          totalQueueLen
            ? t('intentStatus.queueLength', { count: totalQueueLen })
            : t('intentStatus.noPending')
        );
        break;
      }
      case 'pendingMoveRejected':
        ActionLogger.failure('Enqueue move', new Error(detail.reason));
        this.setStatus(t('intentStatus.cannotMove', { reason: detail.reason }));
        break;
      case 'characterMoved':
        this.renderer.setCharacterPosition(detail.position, {
          rotation: detail.rotation,
          immediate: detail.immediate,
        });
        this.setStatus(
          t('intentStatus.moved', {
            tick: detail.tick,
            x: detail.position.x,
            y: detail.position.y,
          })
        );
        break;
      case 'mapTransitionComplete':
        this.transitionLocked = false;
        this.setStatus(t('intentStatus.arrived', { mapId: detail.mapId }));
        this.renderer.setCharacterPosition(this.gameState.getCharacterPosition(), { immediate: true });
        break;
      case 'portalTransitionStart':
        this.cancelActionTargeting();
        this.transitionLocked = true;
        this.setStatus(t('intentStatus.enteringPortal', { mapId: detail.destMapId }));
        break;
      case 'portalTransitionComplete':
        this.transitionLocked = false;
        this.setStatus(t('intentStatus.arrived', { mapId: detail.mapId }));
        this.renderer.setCharacterPosition(detail.position, { immediate: true });
        break;
      case 'portalTransitionCancelled':
        this.transitionLocked = false;
        this.setStatus(t('intentStatus.portalCancelled'));
        break;
      case 'tickUpdated':
        break;
      case 'remotePlayersUpdated':
      case 'mapObjectsUpdated':
      case 'visualAssetsUpdated':
      case 'npcsUpdated':
      case 'runtimeMobsUpdated':
      case 'npcRuntimeUpdated':
      case 'chestInventoryUpdated':
      case 'statusEffectsUpdated':
        this.renderer.requestRender();
        break;
      default:
        assertNever(detail);
    }
  };

  private setStatus(text: string): void {
    if (this.statusEl) {
      this.statusEl.textContent = text;
    }
  }
}

function assertNever(value: never): never {
  throw new Error(`Unhandled detail type ${(value as StateChangeDetail).type}`);
}
